/**
 * **The device benchmark's simulation phase** (`client-benchmark`; design **D-A**, **D-B**,
 * **D-C**, **D-F**).
 *
 * It runs one fixed workload — one colony, no rivals, a fixed seed, a fixed document, 600 s of sim
 * time at 5 Hz — through the **shipped** device path, and reports what actually happened.
 *
 * ## Where it attaches, and why not higher
 *
 * At `createPracticeWorkerHost` (design **D-A**). The shipped practice path layers as
 *
 *     run controller → practice-runner seams → practice-worker-host → worker → waggle-wasm
 *
 * and everything **above** the worker host is tournament machinery: admission, sandbox scope,
 * document commitment, and the server-allocated run id. None of that is simulation, and driving it
 * would need a membership and would count server round-trips as "simulation time". Everything
 * **below** it is. So this is the lowest layer that still contains everything being measured — the
 * real `VmBrain` interpreter and the real trace writer — which is precisely the gap
 * `rounds-practice-sandbox` task 5.14 records: the one phone measurement on file excluded both.
 *
 * ## Three things it deliberately will not do
 *
 * 1. **It will not run against the embedded preset.** The device-side entry point treats its preset
 *    *name* as provenance and resolves the world from supplied TOML, falling back to the embedded
 *    default when none is given. A run naming `benchmark` with no TOML would therefore execute a
 *    15 Hz / 1800 s world while stamping its manifest `benchmark` — a contradiction no trace reader
 *    can detect. So the preset travels as bytes over HTTP (design **D-C**), and a fetch that fails
 *    **fails the phase**; it never falls back. {@link PracticeRunPlan.balanceToml} and
 *    `balancePreset` are passed as the coherent pair that type documents.
 * 2. **It will not touch the player's practice traces.** The shared local store is one origin-wide
 *    root whose publication path evicts the oldest resident run *before* publishing, retaining
 *    five. A benchmark run written there would delete real practice traces and appear in the
 *    sandbox run rail. So the benchmark writes real bytes — publication is a cost the measurement
 *    must include — into its **own** root (design **D-B**), cleared before each run and after a
 *    failed one.
 * 3. **It will not extrapolate.** Per-tick cost grows with population, so multiplying the cheapest
 *    fragment of a run by a horizon would understate a real match by a large unquantified factor.
 *    The report carries segmented throughput and peak population instead, and `report.ts` has no
 *    field a full-match figure could live in.
 *
 * ## What "completed" means here
 *
 * At the shipped solo geometry a colony is measured **not** to survive the 600 s horizon
 * (`solo_survivability_permille: 0`, 50 of 50 eliminated), so a short run is the expected case. The
 * seam reports the horizon it was configured with and the ticks it observed as two separate facts;
 * this phase records the second and derives the terminal condition from the gap. See
 * {@link terminalConditionFor}.
 */

import { decodeCoarse, type CoarseSample } from '../../net/rounds-trace';
import { createPracticeWorkerHost } from '../rounds/practice-worker-host';
import { COMPONENT_FILES, type OpfsDirectoryHandle } from '../rounds/practice-trace-store';
import type {
  PracticeRunPlan,
  PracticeSimulationWorker,
} from '../rounds/practice-runner';
import {
  BENCHMARK_BALANCE_PRESET,
  BENCHMARK_REPORT_PATH,
  BENCHMARK_SEED,
  phaseFailed,
  simSettled,
  type SimPhaseResult,
  type SimTerminalCondition,
  type ThroughputSegment,
} from './report';

// ---------------------------------------------------------------------------------------------
// The workload — fixed, because a benchmark that varied would not compare two devices
// ---------------------------------------------------------------------------------------------

/** Where the `benchmark` preset's TOML is served from. Under the report prefix, never `/benchmark`,
 * which is the client route. */
export const BENCHMARK_PRESET_PATH = `${BENCHMARK_REPORT_PATH}/preset`;

/**
 * The benchmark's own OPFS root — **not** `practice-traces` (design **D-B**).
 *
 * The isolation is the highest-severity property of this change. See
 * `LocalTraceStoreOptions.traceRoot` for the whole argument; in one line, the shared root's publish
 * path evicts before it publishes, so a benchmark run in it would destroy the player's data.
 */
export const BENCHMARK_TRACE_ROOT = 'benchmark-traces';

/**
 * Keep-last-N for the benchmark's own root: **one**.
 *
 * The instrument is expected to be run several times on one borrowed device, and the trace writer
 * is deliberately inside the measured window on devices where quota is tightest. Retention of one,
 * plus {@link clearBenchmarkTraceRoot} before every run, is what makes "repeated runs do not
 * accumulate storage" structural rather than hopeful.
 */
export const BENCHMARK_TRACE_RETENTION = 1;

/**
 * The run id, synthesised rather than allocated.
 *
 * A shipped practice run's id comes from the server's run-start response, which needs a membership,
 * a sandbox scope and an admitted document. The benchmark holds none of those by requirement ("the
 * run needs no tournament membership"), so it names its own — the id is only a directory name and a
 * manifest field, and inside an isolated root it collides with nothing.
 */
export const BENCHMARK_RUN_ID = 1;

/**
 * Solo. WAG-41 specifies a single colony and D11 confirms it stands.
 *
 * **This is now the only caller that states a rival count** (`home-and-single-player`). A *practice*
 * run carries none — the sandbox is a ranked ladder fixed at three defensive rivals, supplied by the
 * engine — but the benchmark is a measurement fixture rather than a played match, and its workload
 * shape is the instrument's own. `PracticeRunPlan.rivals` is optional for exactly this.
 *
 * ponytail: if the Rust side fixes rivals inside `practice_configure` and drops the parameter, this
 * fixture loses its ability to ask for a solo world and the benchmark needs its own configure entry —
 * flagged there rather than silently measuring a denser world than WAG-41 specified.
 */
export const BENCHMARK_RIVALS = 0;

/** 600 s at the `benchmark` preset's 5 Hz. Requested, never assumed to have been reached. */
export const BENCHMARK_TERMINAL_TICK = 3000;

/** The document ordinal recorded in the trace. There is no sandbox behind this run, so it is 1. */
export const BENCHMARK_DOCUMENT_VERSION = 1;

/** How many buckets the run's throughput is reported in. */
export const BENCHMARK_SEGMENT_COUNT = 6;

/**
 * **The whole-colony document the benchmark simulates**, embedded as a literal.
 *
 * A literal for the same reason {@link BENCHMARK_SEED} is one: the workload has to be *identical*
 * on two devices for their numbers to be comparable, and a document fetched from a sandbox would
 * vary with whatever the player last wrote. It is the five shipped preset programs
 * (`waggle-bpp/corpus/presets/*.bpp`, the sources `waggle_round::practice_preset_document` builds
 * from) under DESIGN §3's default birth type, so the measured colony is the shipped one.
 *
 * All five names must be declared: `practice_document_context` puts every preset name in
 * `DocumentContext::required_types`, because a practice colony has no type store behind it and the
 * document is therefore the whole registry.
 */
export const BENCHMARK_DOCUMENT = `birth_type harvester

type harvester buffs capacity

state seek_flowers:
  do wander
  when sees_flowers -> gather
  when heard("flowers") -> follow_lead
  when enemy_within(20) and health < 30 -> flee_home

state follow_lead:
  do move_to(dance_target ?? patch)
  when sees_flowers -> gather
  when enemy_within(20) and health < 30 -> flee_home
  when food < 3 -> flee_home

state gather:
  remember patch = here
  do collect_nectar
  when nectar_full -> deliver
  when not sees_flowers -> drained
  when enemy_within(10) -> flee_home

state drained:
  forget patch
  do wander
  when sees_flowers -> gather
  when heard("flowers") -> follow_lead
  when enemy_within(20) and health < 30 -> flee_home

state deliver:
  do return_to_hive
  when at_hive -> unload

state unload:
  do drop_nectar
  when nectar == 0 and hive_nectar > 800 -> rest
  when nectar == 0 -> seek_flowers

state rest:
  do idle
  when hive_nectar < 700 -> seek_flowers

state flee_home:
  do return_to_hive
  when at_hive and health >= 50 -> seek_flowers

type nurse buffs food_consumption

state tend:
  do feed_larvae
  when not at_hive or food < 3 -> refuel
  when hive_nectar < 300 -> forage

state refuel:
  do return_to_hive
  when at_hive and food >= 3 -> tend

state forage:
  do wander
  when hive_nectar > 600 -> tend
  when sees_flowers -> gather

state gather:
  do collect_nectar
  when nectar_full -> deliver
  when not_gathering -> forage

state deliver:
  do return_to_hive
  when at_hive -> unload

state unload:
  do drop_nectar
  when nectar == 0 and hive_nectar > 600 -> tend
  when nectar == 0 -> forage

type builder buffs building

state expand:
  do build
  when cell_damaged -> mend
  when hive_nectar < 10 -> waiting

state waiting:
  do idle
  when cell_damaged -> mend
  when hive_nectar >= 10 -> expand

state mend:
  do repair
  when not cell_damaged -> expand

type scout buffs speed

state search:
  do wander
  when sees_flowers -> found

state found:
  remember patch = here
  do dance(patch, "flowers")
  when not sees_flowers -> search

type warrior buffs strength

state guard:
  do return_to_hive
  when health < 30 -> recover
  when enemy_within(20) -> engage

state engage:
  do fight
  when health < 30 -> recover
  when food < 3 and not at_hive -> refuel
  when distance_to_hive > 60 -> guard
  when not sees_enemy -> guard

state refuel:
  do return_to_hive
  when at_hive and food >= 8 -> guard
  when at_hive and enemy_within(20) -> engage

state recover:
  do return_to_hive
  when health >= 30 -> guard
`;

/**
 * The frozen plan the benchmark runs.
 *
 * `balanceToml` and `balancePreset` are passed **together**, which is the only coherent shape the
 * runner accepts and the reason the manifest's declared preset and the resolved world are the same
 * fact rather than two hopeful ones.
 */
export function benchmarkRunPlan(balanceToml: string): PracticeRunPlan {
  return {
    runId: BENCHMARK_RUN_ID,
    documentVersion: BENCHMARK_DOCUMENT_VERSION,
    document: BENCHMARK_DOCUMENT,
    rivals: BENCHMARK_RIVALS,
    seed: BENCHMARK_SEED,
    terminalTick: BENCHMARK_TERMINAL_TICK,
    balanceToml,
    balancePreset: BENCHMARK_BALANCE_PRESET,
  };
}

// ---------------------------------------------------------------------------------------------
// The preset fetch
// ---------------------------------------------------------------------------------------------

/** What the preset fetch answers. A typed refusal, as a value — never a throw (`net/rounds-http.ts`'s
 * house rule). */
export type BenchmarkPresetFetch =
  | { readonly kind: 'preset'; readonly toml: string }
  | { readonly kind: 'unavailable'; readonly reason: string };

type FetchLike = (url: string, init?: RequestInit) => Promise<Response>;

/**
 * Fetches the `benchmark` preset's TOML.
 *
 * Deliberately unauthenticated and credential-free: the device already downloaded the whole client
 * from this server, so no membership, join code or token is involved — and requiring one would put
 * an operator secret on every borrowed test device.
 */
export async function fetchBenchmarkPreset(
  doFetch: FetchLike = (url, init) => fetch(url, init),
): Promise<BenchmarkPresetFetch> {
  let response: Response;
  try {
    response = await doFetch(BENCHMARK_PRESET_PATH, {
      method: 'GET',
      credentials: 'omit',
      headers: { accept: 'text/toml, text/plain' },
    });
  } catch (error) {
    return {
      kind: 'unavailable',
      reason: `the preset request could not be sent: ${
        error instanceof Error ? error.message : String(error)
      }`,
    };
  }
  if (!response.ok) {
    return { kind: 'unavailable', reason: `the preset request answered ${response.status}` };
  }
  let toml: string;
  try {
    toml = await response.text();
  } catch (error) {
    void error;
    return { kind: 'unavailable', reason: 'the preset response body could not be read' };
  }
  // An empty body is not a preset. Passing `''` on would be worse than refusing: the runner would
  // see a present-but-malformed balance rather than an absent one, and the failure would surface
  // as a configuration refusal with no hint of where it came from.
  if (toml.trim().length === 0) {
    return { kind: 'unavailable', reason: 'the preset response was empty' };
  }
  return { kind: 'preset', toml };
}

// ---------------------------------------------------------------------------------------------
// The phase
// ---------------------------------------------------------------------------------------------

export interface BenchmarkSimPhaseDeps {
  /** Fetches the served preset TOML. Defaults to {@link fetchBenchmarkPreset}. */
  fetchPreset?: () => Promise<BenchmarkPresetFetch>;
  /**
   * Builds the run's worker over the given trace root. Defaults to the shipped
   * `createPracticeWorkerHost` — the seam design **D-A** attaches at.
   */
  createWorker?: (traceRoot: string) => PracticeSimulationWorker;
  /** The origin-private root. Defaults to `navigator.storage.getDirectory()`. */
  openRoot?: () => Promise<OpfsDirectoryHandle>;
  /** Monotonic milliseconds. Defaults to `performance.now()`. */
  now?: () => number;
}

/** Ticks stepped, against the wall-clock moment the report for them arrived. */
interface ProgressSample {
  readonly ticks: number;
  readonly at: number;
}

/**
 * How the run ended, from the two facts the seam reports.
 *
 * The worker seam surfaces the configured **horizon** (`terminalTick`, which never moves) and the
 * ticks it **observed** as separate values, and does not surface the engine's own terminal-condition
 * label. So the honest derivation is the gap between them: a run that stopped short of its horizon
 * stopped because the measured colony was lost, and one that reached it ran the clock out.
 */
export function terminalConditionFor(
  ticksStepped: number,
  requestedTicks: number,
): SimTerminalCondition {
  return ticksStepped >= requestedTicks ? 'clock_expired' : 'colony_lost';
}

/**
 * Runs the phase.
 *
 * Never throws: every failure is a {@link SimPhaseResult} with `status: 'failed'`, because the
 * screen has to be able to *show* a failed phase and the submitted report has to record it — an
 * absent phase and a failed phase are different facts, and only one of them is true.
 */
export async function runBenchmarkSimPhase(
  deps: BenchmarkSimPhaseDeps = {},
): Promise<SimPhaseResult> {
  const now = deps.now ?? ((): number => performance.now());
  const fetchPreset = deps.fetchPreset ?? ((): Promise<BenchmarkPresetFetch> => fetchBenchmarkPreset());
  const createWorker =
    deps.createWorker ??
    ((traceRoot: string): PracticeSimulationWorker =>
      createPracticeWorkerHost({ traceRoot, retain: BENCHMARK_TRACE_RETENTION }));
  const openRoot =
    deps.openRoot ??
    (async (): Promise<OpfsDirectoryHandle> =>
      (await navigator.storage.getDirectory()) as unknown as OpfsDirectoryHandle);

  const startedAt = now();

  // -- the preset, BEFORE anything is created ---------------------------------------------------
  //
  // Ordered first on purpose: a run that fell back to the embedded default would execute a
  // different world while stamping the manifest `benchmark`. Refusing here means no worker is
  // spawned, no directory is created and no tick is stepped — "no run is executed against the
  // embedded default" is true of the code path, not merely of its outcome.
  const preset = await fetchPreset();
  if (preset.kind === 'unavailable') {
    return phaseFailed('preset_unavailable');
  }

  let root: OpfsDirectoryHandle;
  try {
    root = await openRoot();
  } catch (error) {
    void error;
    return phaseFailed('storage_unavailable');
  }

  // Cleared at the START of each run, so repeated benchmarking on one borrowed device cannot grow
  // without bound and so a previous run's partial bytes cannot be mistaken for this one's.
  await clearBenchmarkTraceRoot(root);

  const worker = createWorker(BENCHMARK_TRACE_ROOT);
  const plan = benchmarkRunPlan(preset.toml);

  const progress: ProgressSample[] = [];
  // The coarse tier as it crosses the worker boundary while the run is watched. Merged below with
  // the published tier, which covers the whole run — see `collectCoarseSamples`.
  const watchedCoarse = new Map<number, CoarseSample>();

  let steppingStartedAt: number | null = null;
  let steppingEndedAt: number | null = null;

  let result;
  try {
    result = await worker.run(
      plan,
      (report) => {
        const at = now();
        // The first report lands *before* the first tick is stepped (the driver reports its
        // determinate horizon on entry), which is what makes it the honest zero of the stepping
        // window: module load and the store's own open are already behind it.
        if (steppingStartedAt === null) steppingStartedAt = at;
        steppingEndedAt = at;
        progress.push({ ticks: report.ticksObserved, at });
      },
      (view) => {
        for (const sample of view.snapshot.coarseSamples) watchedCoarse.set(sample.tick, sample);
      },
    );
  } catch (error) {
    // The seam contracts not to throw, so reaching here is something below it exploding. Still a
    // value, and still cleaned up.
    void error;
    await clearBenchmarkTraceRoot(root);
    worker.terminate();
    return phaseFailed('worker_crashed');
  }
  // One worker per run, released whatever the outcome.
  worker.terminate();

  if (result.kind !== 'completed') {
    // A failed or cancelled run leaves no partial trace behind: the driver already discards its own
    // generation, and this removes the root outright so nothing survives even a failure below it.
    await clearBenchmarkTraceRoot(root);
    return phaseFailed(result.kind === 'cancelled' ? 'cancelled' : result.reason);
  }

  const ticksStepped = progress.length === 0 ? 0 : progress[progress.length - 1]!.ticks;
  const coarse = await collectCoarseSamples(root, watchedCoarse);

  return simSettled({
    terminalCondition: terminalConditionFor(ticksStepped, BENCHMARK_TERMINAL_TICK),
    ticksStepped,
    requestedTicks: BENCHMARK_TERMINAL_TICK,
    peakBees: peakBeesIn(coarse),
    segments: segmentsOf(progress, coarse),
    // Two DISTINCT figures, so a slow device is diagnosable as CPU-bound rather than storage-bound
    // (or the reverse). `steppingMs` is the window between the first and last progress report —
    // stepping plus the per-tick trace writes, which is the thing the sim costs; `totalMs` adds
    // module load, the store's open, the final flush and the atomic publication.
    steppingMs:
      steppingStartedAt === null || steppingEndedAt === null
        ? 0
        : steppingEndedAt - steppingStartedAt,
    totalMs: now() - startedAt,
    seed: BENCHMARK_SEED,
    balancePreset: BENCHMARK_BALANCE_PRESET,
  });
}

// ---------------------------------------------------------------------------------------------
// The isolated root
// ---------------------------------------------------------------------------------------------

/**
 * Removes the benchmark's whole root.
 *
 * Called before every run (so repeated benchmarking is bounded) and after a failed one (so no
 * partial trace is left behind). It names {@link BENCHMARK_TRACE_ROOT} and nothing else, so it can
 * never reach the player's `practice-traces`; an absent root is the state it wants and is not an
 * error.
 */
export async function clearBenchmarkTraceRoot(root: OpfsDirectoryHandle): Promise<void> {
  try {
    await root.removeEntry(BENCHMARK_TRACE_ROOT, { recursive: true });
  } catch (error) {
    // `NotFoundError` is success. Anything else is a medium that will fail the run itself in a
    // moment and report a cause of its own; swallowing here keeps that cause visible.
    void error;
  }
}

// ---------------------------------------------------------------------------------------------
// The population curve
// ---------------------------------------------------------------------------------------------

/**
 * The run's coarse tier, from both places it is legible.
 *
 * The watch stream crosses the worker boundary *during* the run but is bounded to the run's head
 * window (`DEFAULT_WATCH_WINDOW_TICKS`), so on a run that outlives the window it covers only the
 * beginning. The published coarse component covers the whole run and is small (the coarse tier is
 * a sampled summary, not the detail tier), so it is read back after the run has settled — outside
 * the measured window, since it is analysis and not simulation.
 *
 * Best-effort by design: an unreadable or absent component yields whatever the watch stream saw,
 * and an empty result means "not observed" rather than "zero bees". Nothing here can fail the
 * phase — a measurement that ran is not thrown away because its population curve would not decode.
 */
async function collectCoarseSamples(
  root: OpfsDirectoryHandle,
  watched: ReadonlyMap<number, CoarseSample>,
): Promise<readonly CoarseSample[]> {
  const byTick = new Map<number, CoarseSample>(watched);
  try {
    const store = await root.getDirectoryHandle(BENCHMARK_TRACE_ROOT);
    const run = await store.getDirectoryHandle(String(BENCHMARK_RUN_ID));
    const handle = await run.getFileHandle(COMPONENT_FILES.coarse);
    const bytes = new Uint8Array(await (await handle.getFile()).arrayBuffer());
    for (const sample of decodeCoarse(bytes).samples) byTick.set(sample.tick, sample);
  } catch (error) {
    void error;
  }
  return [...byTick.values()].sort((a, b) => a.tick - b.tick);
}

/** The bees alive in a coarse sample, across every colony it lists. */
function beesIn(sample: CoarseSample): number {
  return sample.colonies.reduce((total, colony) => total + colony.population, 0);
}

/** The highest bee count seen across the run. `0` when the tier was not observed at all. */
function peakBeesIn(samples: readonly CoarseSample[]): number {
  return samples.reduce((peak, sample) => Math.max(peak, beesIn(sample)), 0);
}

/** The bee count at or before `tick`, from the samples available. */
function beesAt(samples: readonly CoarseSample[], tick: number): number {
  let found = 0;
  for (const sample of samples) {
    if (sample.tick > tick) break;
    found = beesIn(sample);
  }
  return found;
}

/**
 * Buckets the run into {@link BENCHMARK_SEGMENT_COUNT} throughput segments.
 *
 * Segmented rather than averaged because per-tick cost rises with colony size: a single figure
 * taken over a colony that grew and then collapsed describes no part of the run. Each segment
 * carries the population at its end for the same reason — the numbers are only readable together.
 *
 * A segment whose wall-clock span the injected clock could not resolve reports `0` rather than a
 * fabricated infinity; `0` here reads as "not measurable", which is true, where a huge number would
 * read as "extremely fast", which would not be.
 */
export function segmentsOf(
  progress: readonly ProgressSample[],
  samples: readonly CoarseSample[],
): readonly ThroughputSegment[] {
  if (progress.length < 2) return [];
  const total = progress[progress.length - 1]!.ticks;
  if (total <= 0) return [];

  const segments: ThroughputSegment[] = [];
  for (let i = 0; i < BENCHMARK_SEGMENT_COUNT; i += 1) {
    const fromTick = Math.floor((i * total) / BENCHMARK_SEGMENT_COUNT);
    const toTickExclusive = Math.floor(((i + 1) * total) / BENCHMARK_SEGMENT_COUNT);
    const ticks = toTickExclusive - fromTick;
    if (ticks <= 0) continue;
    const startedAt = timeAt(progress, fromTick);
    const endedAt = timeAt(progress, toTickExclusive);
    const elapsedMs = endedAt - startedAt;
    segments.push({
      fromTick,
      toTick: toTickExclusive - 1,
      ticksPerSecond: elapsedMs > 0 ? (ticks * 1000) / elapsedMs : 0,
      bees: beesAt(samples, toTickExclusive - 1),
    });
  }
  return segments;
}

/** The wall-clock moment the run had stepped exactly `ticks` ticks. */
function timeAt(progress: readonly ProgressSample[], ticks: number): number {
  let at = progress[0]!.at;
  for (const sample of progress) {
    if (sample.ticks > ticks) break;
    at = sample.at;
  }
  return at;
}
